import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LabelLayout,
  LabelTemplateTypeName,
} from '@enterprise-wms/domain';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';
import { devicePrinterHeaders } from '@/stores/device-printer-store';
import type { PrintJob } from './usePrinting';

export type LabelTemplateType = LabelTemplateTypeName;
export type LabelSizeKey = '4x2' | '4x3' | '4x6';

export interface LabelTemplate {
  id: string;
  name: string;
  type: LabelTemplateType;
  /** null = GLOBAL fallback, usable by every warehouse. */
  warehouseId: string | null;
  isDefault: boolean;
  isActive: boolean;
  schemaVersion: number;
  widthDots: number;
  heightDots: number;
  dpi: number;
  layout: LabelLayout;
  description: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** Non-blocking advisories — currently only the deactivate-the-default case. */
  warnings?: string[];
}

export interface LabelPreset {
  key: string;
  name: string;
  /** null = blank preset; the type is chosen at create time. */
  type: LabelTemplateType | null;
  size: LabelSizeKey;
  widthDots: number;
  heightDots: number;
  layout: LabelLayout;
  description: string;
}

export interface BindableField {
  token: string;
  label: string;
  sample: string;
}

export interface ListLabelTemplatesParams {
  warehouseId: string;
  type?: LabelTemplateType;
  includeGlobal?: boolean;
  activeOnly?: boolean;
}

/**
 * The Label Templates page and its PrintSitesCard must share ONE list query —
 * same key, one request, and the card sees the same UNFILTERED rows the page
 * manages. Building the params in one place makes that structural: a filter
 * added to the page's query lands in the card too (or diverges deliberately,
 * in review).
 */
export function labelTemplatesListParams(
  warehouseId: string | null,
): ListLabelTemplatesParams {
  return { warehouseId: warehouseId ?? '' };
}

export function useLabelTemplates(params: ListLabelTemplatesParams) {
  return useQuery({
    queryKey: queryKeys.labelTemplates.list({ ...params }),
    queryFn: async () => {
      const { data } = await apiClient.get<LabelTemplate[]>(
        '/api/label-templates',
        { params },
      );
      return data;
    },
    enabled: !!params.warehouseId,
  });
}

export function useLabelTemplate(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.labelTemplates.detail(id ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<LabelTemplate>(
        `/api/label-templates/${id}`,
      );
      return data;
    },
    // 'new' is the unsaved-draft sentinel, not a fetchable id.
    enabled: !!id && id !== 'new',
  });
}

export function useFieldCatalog(type: LabelTemplateType | undefined) {
  return useQuery({
    queryKey: queryKeys.labelTemplates.fieldCatalog(type ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<BindableField[]>(
        '/api/label-templates/field-catalog',
        { params: { type } },
      );
      return data;
    },
    enabled: !!type,
  });
}

export function useLabelPresets(params?: {
  type?: LabelTemplateType;
  size?: LabelSizeKey;
}) {
  return useQuery({
    queryKey: queryKeys.labelTemplates.presets(params?.type, params?.size),
    queryFn: async () => {
      const { data } = await apiClient.get<LabelPreset[]>(
        '/api/label-templates/presets',
        { params },
      );
      return data;
    },
  });
}

export interface CreateLabelTemplateParams {
  name: string;
  type: LabelTemplateType;
  /** Omit for a GLOBAL template — admin only, 403 otherwise. */
  warehouseId?: string;
  isDefault?: boolean;
  isActive?: boolean;
  widthDots: number;
  heightDots: number;
  layout: LabelLayout;
  description?: string;
}

export function useCreateLabelTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateLabelTemplateParams) => {
      const { data } = await apiClient.post<LabelTemplate>(
        '/api/label-templates',
        params,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelTemplates.all,
      });
    },
  });
}

/** `type` and `warehouseId` are immutable server-side — changing scope is a create + delete. */
export interface UpdateLabelTemplateParams {
  id: string;
  name?: string;
  isActive?: boolean;
  widthDots?: number;
  heightDots?: number;
  layout?: LabelLayout;
  description?: string;
}

export function useUpdateLabelTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: UpdateLabelTemplateParams) => {
      const { data } = await apiClient.patch<LabelTemplate>(
        `/api/label-templates/${id}`,
        body,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelTemplates.all,
      });
    },
  });
}

export function useSetDefaultLabelTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.patch<LabelTemplate>(
        `/api/label-templates/${id}/default`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelTemplates.all,
      });
    },
  });
}

/** The inverse of set-default: the type falls back to the built-in layout. */
export function useClearDefaultLabelTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.delete<LabelTemplate>(
        `/api/label-templates/${id}/default`,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelTemplates.all,
      });
    },
  });
}

export function useDeleteLabelTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/label-templates/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.labelTemplates.all,
      });
    },
  });
}

export interface PreviewLabelTemplateParams {
  widthDots: number;
  heightDots: number;
  layout: LabelLayout;
  values?: Record<string, string>;
  /** Render for this printhead resolution; omitted = the layout's authoring dpi. */
  targetDpi?: number;
  /**
   * The chosen printer's stock and orientation. With these the server fits
   * the template exactly as a print to that printer would (a ROTATED or FILL
   * printer scales, centres and may turn it); without them only dpi applies.
   */
  targetLabelSize?: string;
  targetOrientation?: 'UPRIGHT' | 'ROTATED';
  targetLayout?: 'CLASSIC' | 'FILL';
}

export interface RenderedLabel {
  zpl: string;
  warnings: string[];
}

/**
 * Pure render — takes no entity id, so the designer can never be used to read
 * another warehouse's data. Real-data binding happens only on the
 * warehouse-gated print route below.
 */
export function usePreviewLabelTemplate() {
  return useMutation({
    mutationFn: async (params: PreviewLabelTemplateParams) => {
      const { data } = await apiClient.post<RenderedLabel>(
        '/api/label-templates/preview',
        params,
      );
      return data;
    },
  });
}

export interface PrintTemplateLabelParams {
  warehouseId: string;
  /** Exactly one of templateId / type — 400 otherwise. */
  templateId?: string;
  type?: LabelTemplateType;
  subjectIds?: string[];
  values?: Record<string, string>;
  /** Uniform count. Mutually exclusive with `copiesFrom` — 400 if both. */
  copies?: number;
  /**
   * Bindable token giving the PER-SUBJECT label count, e.g.
   * `orderLine.qtyOrdered` — 5 of SKU A and 6 of SKU B emit 11 labels in one
   * job. Mutually exclusive with `copies`.
   */
  copiesFrom?: string;
  title?: string;
}

export interface PrintTemplateLabelResponse {
  job: PrintJob;
  templateId: string;
  templateName: string;
  labelCount: number;
  warnings: string[];
  /** The minted codes, in emission order. Absent unless the template uses a
   *  `{{labelSerial.*}}` token. */
  serials?: string[];
}

/**
 * The per-device printer override is NOT a global interceptor — it must be
 * attached per call, exactly like the other print-triggering mutations.
 */
export function usePrintTemplateLabel() {
  return useMutation({
    mutationFn: async (params: PrintTemplateLabelParams) => {
      const { data } = await apiClient.post<PrintTemplateLabelResponse>(
        '/api/printing/template-labels',
        params,
        { headers: devicePrinterHeaders() },
      );
      return data;
    },
  });
}
